import { Box, styled, Typography } from '@mui/material';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { ReservationFormInput } from '../schema/reservationForm.schema';
import {
  isAppointmentDateRequired,
  isAppointmentDateTimeRequired,
  isPickupDateTimeRequired,
} from '../schema/reservationForm.schema';
import { TripArrayFieldDateInput } from './TripArrayFieldDateInput';
import { ReasonForTripField } from './ReasonForTripField';
import { TripArrayFieldDateTimeInput } from './TripArrayFieldDateTimeInput';
import { WillCallSelector } from './WillCallSelector';
import { TripArrayFieldPhoneInput } from './TripArrayFieldPhoneInput';
import { PickupNotesField } from './PickupNotesField';
import { DropoffNotesField } from './DropoffNotesField';
import { PickupAddressField } from './PickupAddressField';
import { DropoffAddressField } from './DropoffAddressField';
import { DropoffAddressValidationMessage } from '../messages/DropoffAddressValidationMessage';
import { LevelOfCareSection } from './LevelOfCareSection';
import { RoundTripReturnTripFields } from './RoundTripReturnTripFields';
import { NeedsApprovalMessage } from '../messages/NeedsApprovalMessage';

const PREFIX = 'TripLegFieldsRow';

const classes = {
  container: `${PREFIX}-container`,
  fieldSection: `${PREFIX}-fieldSection`,
}

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  [`& .${classes.fieldSection}`]: {
    marginTop: theme.spacing(4),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
}));

export function TripLegFieldsRow({
  index,
  tripLabel,
  showRoundTripReturnFields = false,
}: {
  index: number;
  tripLabel?: string;
  showRoundTripReturnFields?: boolean;
}) {
  const { t } = useTranslation('common');
  const { control } = useFormContext<ReservationFormInput>();
  const isWillCall =
    useWatch({
      control,
      name: `trips.${index}.isWillCall`,
    }) ?? false;

  return (
    <StyledBox className={classes.container}>
      {tripLabel ? (
        <Typography variant="subtitle1" fontWeight={600}>
          {tripLabel}
        </Typography>
      ) : null}
      <ReasonForTripField index={index} />

      {isWillCall ? (
        <TripArrayFieldDateInput
          tripField="appointmentDate"
          index={index}
          required={isAppointmentDateRequired(isWillCall)}
        />
      ) : (
        <TripArrayFieldDateTimeInput
          tripField="appointmentDateTime"
          index={index}
          required={isAppointmentDateTimeRequired(isWillCall)}
        />
      )}

      <Box className={classes.fieldSection}>
        <WillCallSelector index={index} />

        {!isWillCall && (
          <TripArrayFieldDateTimeInput
            tripField="pickupDateTime"
            index={index}
            required={isPickupDateTimeRequired(isWillCall)}
          />
        )}

        <PickupAddressField index={index} />
        <PickupNotesField index={index} />
        <TripArrayFieldPhoneInput
          index={index}
          tripField="pickupPhone"
          extensionField="pickupPhoneExtension"
          label={t('member.phone')}
          required
        />
      </Box>

      <Box className={classes.fieldSection}>
        <Typography variant="h5" component="h3">
          {t('member.dropOff')}
        </Typography>
        
        <DropoffAddressField index={index} />
        <DropoffAddressValidationMessage index={index} />
        <DropoffNotesField index={index} />
        <TripArrayFieldPhoneInput
          index={index}
          tripField="dropoffPhone"
          extensionField="dropoffPhoneExtension"
          label={t('member.phone')}
        />
      </Box>

      {showRoundTripReturnFields ? (
        <RoundTripReturnTripFields index={index} />
      ) : null}

      <Box className={classes.fieldSection}>
        <LevelOfCareSection index={index} />
        <NeedsApprovalMessage index={index} />
      </Box>
    </StyledBox>
  );
}
